/** 全局滚动条：右侧 20px 热区淡入；移出 1s 后颜色渐变淡出（伪元素不支持 transition，淡出由 JS 驱动 alpha） */
const HIDE_DELAY_MS = 1000
const FADE_OUT_MS = 1000
const HOVER_ZONE_WIDTH = 20

const VISIBLE_CLASS = 'yt-scrollbar-visible'
const THUMB_ALPHA_VAR = '--yt-scrollbar-thumb-alpha'

const visibleTargets = new Set<Element>()
const activeFades = new WeakMap<Element, () => void>()
let hideDelayTimer: ReturnType<typeof setTimeout> | undefined
let activeHoverTarget: Element | null = null

function cancelHideTimers() {
  if (hideDelayTimer) {
    clearTimeout(hideDelayTimer)
    hideDelayTimer = undefined
  }
}

function cancelActiveFade(el: Element) {
  activeFades.get(el)?.()
  activeFades.delete(el)
}

function clearThumbAlphaStyle(el: HTMLElement) {
  el.style.removeProperty(THUMB_ALPHA_VAR)
  if (el.classList.contains('el-scrollbar')) {
    getElScrollbarBars(el).forEach((bar) => bar.style.removeProperty('opacity'))
  }
}

/** Element Plus 自定义滚动条：类加在 .el-scrollbar 根节点上 */
function getScrollbarHost(el: Element): Element {
  if (el.classList.contains('el-scrollbar__wrap')) {
    return el.closest('.el-scrollbar') ?? el
  }
  return el
}

function getElScrollbarBars(host: Element): HTMLElement[] {
  return Array.from(host.querySelectorAll('.el-scrollbar__bar')) as HTMLElement[]
}

function isOverflowScrollable(value: string) {
  return value === 'auto' || value === 'scroll' || value === 'overlay'
}

function canScrollY(el: HTMLElement) {
  const style = getComputedStyle(el)
  if (!isOverflowScrollable(style.overflowY)) return false
  return el.scrollHeight > el.clientHeight + 1
}

function isInVerticalHotZone(clientX: number, clientY: number, rect: DOMRect) {
  if (clientY < rect.top || clientY > rect.bottom) return false
  return clientX >= rect.right - HOVER_ZONE_WIDTH && clientX <= rect.right + 1
}

function isDocumentScrollable() {
  const root = document.scrollingElement ?? document.documentElement
  return root.scrollHeight > root.clientHeight + 1
}

function isInViewportRightHotZone(clientX: number, clientY: number) {
  if (!isDocumentScrollable()) return false
  if (clientY < 0 || clientY > window.innerHeight) return false
  return window.innerWidth - clientX <= HOVER_ZONE_WIDTH
}

/** 从指针位置向上找「右侧热区内」的最内层可滚动容器 */
function findScrollbarTarget(clientX: number, clientY: number): Element | null {
  const chain: HTMLElement[] = []
  let node = document.elementFromPoint(clientX, clientY) as HTMLElement | null

  while (node) {
    if (canScrollY(node)) chain.push(node)
    node = node.parentElement
  }

  for (const el of chain) {
    const host = getScrollbarHost(el)
    const rect = host.getBoundingClientRect()
    if (isInVerticalHotZone(clientX, clientY, rect)) {
      return host
    }
  }

  if (isInViewportRightHotZone(clientX, clientY)) {
    return getScrollbarHost(document.scrollingElement ?? document.documentElement)
  }

  return null
}

function setThumbAlpha(host: HTMLElement, alpha: number) {
  const value = alpha.toFixed(4)
  host.style.setProperty(THUMB_ALPHA_VAR, value)
  if (host.classList.contains('el-scrollbar')) {
    getElScrollbarBars(host).forEach((bar) => {
      bar.style.opacity = value
    })
  }
}

/** 在宿主节点上过渡 alpha，供伪元素 rgba(...) 引用（::-webkit-scrollbar-thumb 本身不能 transition） */
function runThumbAlphaFade(host: HTMLElement, onDone: () => void) {
  cancelActiveFade(host)

  const start = performance.now()
  let rafId = 0

  const cleanup = () => {
    if (rafId) cancelAnimationFrame(rafId)
    clearThumbAlphaStyle(host)
    activeFades.delete(host)
  }

  activeFades.set(host, cleanup)

  const tick = (now: number) => {
    const progress = Math.min(1, (now - start) / FADE_OUT_MS)
    setThumbAlpha(host, 1 - progress)

    if (progress < 1) {
      rafId = requestAnimationFrame(tick)
      return
    }

    cleanup()
    onDone()
  }

  setThumbAlpha(host, 1)
  rafId = requestAnimationFrame(tick)
}

function clearOtherTargets(keep: Element) {
  visibleTargets.forEach((el) => {
    if (el === keep) return
    cancelActiveFade(el)
    el.classList.remove(VISIBLE_CLASS)
    clearThumbAlphaStyle(el as HTMLElement)
    visibleTargets.delete(el)
  })
}

function revealScrollbar(target: Element) {
  cancelHideTimers()
  cancelActiveFade(target)
  clearOtherTargets(target)
  visibleTargets.add(target)

  const host = target as HTMLElement
  clearThumbAlphaStyle(host)
  target.classList.add(VISIBLE_CLASS)
}

function startFadeOut() {
  if (activeHoverTarget || visibleTargets.size === 0) return

  cancelHideTimers()

  const fading = Array.from(visibleTargets).filter((el) => el.classList.contains(VISIBLE_CLASS))
  fading.forEach((el) => {
    const host = el as HTMLElement
    host.classList.remove(VISIBLE_CLASS)
    runThumbAlphaFade(host, () => {
      visibleTargets.delete(el)
    })
  })
}

function scheduleHideAfterIdle() {
  if (activeHoverTarget) return
  cancelHideTimers()
  hideDelayTimer = setTimeout(() => {
    hideDelayTimer = undefined
    startFadeOut()
  }, HIDE_DELAY_MS)
}

function onMouseMove(e: MouseEvent) {
  const target = findScrollbarTarget(e.clientX, e.clientY)

  if (target) {
    if (activeHoverTarget !== target) {
      activeHoverTarget = target
      revealScrollbar(target)
    } else if (!target.classList.contains(VISIBLE_CLASS) || activeFades.has(target)) {
      revealScrollbar(target)
    } else {
      cancelHideTimers()
    }
    return
  }

  if (activeHoverTarget) {
    activeHoverTarget = null
    scheduleHideAfterIdle()
  }
}

export function initGlobalScrollbar() {
  window.addEventListener('mousemove', onMouseMove, { passive: true })
}
